'use client';

import { useMemo, useRef, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box } from '@terra/ui-styled-system';
import {
  MetaDefault,
  ObjectEditField,
  ObjectEditFieldGroup,
  ObjectEditSection,
  OwaspRiskCalculator,
  type EditableFields,
} from '@terra/ui-design-system';
import type { OwaspRiskFactorKey } from '@terra/shared-cvss';
import { FindingCopilotNote } from '../object-context/FindingCopilotNote';
import { useCopilotReviewArrival } from '../../hooks/useCopilotReviewArrival';
import {
  getOwaspFactorChanges,
  getOwaspScoreTransition,
  hasOwaspVectorChanged,
  resolveOwaspSuggestionBefore,
} from './findingEditOwaspSection.utils';
import type { FindingEditMarkableFieldProps } from './findingEditForm.types';

export type FindingEditOwaspSectionProps = {
  editableFields: EditableFields;
  findingCreatedAt: string | Date | null | undefined;
  owaspMarkableFieldProps?: FindingEditMarkableFieldProps;
  onScoreChange: (score: string, vector: string, level: string) => void;
};

export function FindingEditOwaspSection({
  editableFields,
  findingCreatedAt,
  owaspMarkableFieldProps,
  onScoreChange,
}: FindingEditOwaspSectionProps) {
  const t = useTranslations('designSystem.findings.editDrawer');
  const tReview = useTranslations(
    'designSystem.findings.editDrawer.suggestionReview',
  );
  const fieldRef = useRef<HTMLDivElement>(null);
  const isAcceptedFlash = Boolean(owaspMarkableFieldProps?.isAcceptedFlash);
  const isReviewing = Boolean(
    owaspMarkableFieldProps?.isReviewing && !isAcceptedFlash,
  );
  const { washPortal } = useCopilotReviewArrival({
    isReviewing,
    reviewArrivalAt: isReviewing
      ? owaspMarkableFieldProps?.reviewArrivalAt
      : undefined,
    elementRef: fieldRef,
  });
  const pendingEdit = owaspMarkableFieldProps?.pendingEdit;
  const hasPendingOwasp = Boolean(
    pendingEdit &&
      owaspMarkableFieldProps?.onAcceptPendingEdit &&
      owaspMarkableFieldProps?.onDiscardPendingEdit,
  );
  const suggestionSides = useMemo(() => {
    if (!pendingEdit) {
      return null;
    }

    return {
      before: resolveOwaspSuggestionBefore({
        pendingBefore: pendingEdit.before,
        pendingAfter: pendingEdit.after,
        original: owaspMarkableFieldProps?.baselineValue,
      }),
      after: pendingEdit.after,
    };
  }, [owaspMarkableFieldProps?.baselineValue, pendingEdit]);
  const factorChanges = useMemo(
    () =>
      suggestionSides
        ? getOwaspFactorChanges({
            before: suggestionSides.before,
            after: suggestionSides.after,
          })
        : [],
    [suggestionSides],
  );
  const factorFooters = useMemo(() => {
    if (!hasPendingOwasp || factorChanges.length === 0) {
      return undefined;
    }

    return factorChanges.reduce<Partial<Record<OwaspRiskFactorKey, ReactNode>>>(
      (footers, change) => {
        footers[change.field] = (
          <FindingCopilotNote
            variant="affected-transition"
            beforeLabel={change.beforeLabel}
            afterLabel={change.afterLabel}
          />
        );

        return footers;
      },
      {},
    );
  }, [factorChanges, hasPendingOwasp]);
  const scoreTransition = suggestionSides
    ? getOwaspScoreTransition({
        before: suggestionSides.before,
        after: suggestionSides.after,
      })
    : null;
  const vectorChanged =
    suggestionSides &&
    hasOwaspVectorChanged({
      before: suggestionSides.before,
      after: suggestionSides.after,
    });
  const changedFactorCount = Math.max(
    factorChanges.length,
    vectorChanged ? 1 : 0,
  );
  const copilotChrome = hasPendingOwasp
    ? {
        groupAlert: (
          <FindingCopilotNote
            variant="group-message"
            message={tReview('riskFactorsChanged', {
              count: changedFactorCount > 0 ? changedFactorCount : 1,
            })}
            isFocused={isReviewing}
            onAccept={owaspMarkableFieldProps?.onAcceptPendingEdit}
            onDiscard={owaspMarkableFieldProps?.onDiscardPendingEdit}
          />
        ),
        vectorFooter: vectorChanged ? (
          <FindingCopilotNote
            variant="affected-label"
            label={tReview('recalculated')}
          />
        ) : undefined,
        riskScoreFooter: scoreTransition ? (
          <FindingCopilotNote
            variant="affected-transition"
            beforeLabel={scoreTransition.beforeLabel}
            afterLabel={scoreTransition.afterLabel}
          />
        ) : undefined,
        factorFooters,
        isReviewing,
      }
    : isAcceptedFlash
      ? {
          groupAlert: <FindingCopilotNote variant="accepted" />,
        }
      : undefined;
  const isOwaspComputing =
    !editableFields.owaspRisk?.level &&
    !!findingCreatedAt &&
    Date.now() - new Date(findingCreatedAt).getTime() < 30 * 60 * 1000;

  return (
    <ObjectEditSection
      title={t('sections.owaspRiskAssessment.title')}
      titleAddon={
        <MetaDefault color="fg.muted">
          {isOwaspComputing
            ? t('sections.owaspRiskAssessment.computingDescription')
            : t('sections.owaspRiskAssessment.description')}
        </MetaDefault>
      }
    >
      <ObjectEditFieldGroup>
        <ObjectEditField span="full">
          <Box
            ref={fieldRef}
            data-field-anchor="owaspRisk"
            data-pending-review-field={
              hasPendingOwasp ? 'owaspRisk' : undefined
            }
            w="full"
            borderRadius="md"
          >
            {washPortal}
            <OwaspRiskCalculator
              initialScore={editableFields.owaspRisk?.score || ''}
              initialVector={editableFields.owaspRisk?.vector || ''}
              copilotChrome={copilotChrome}
              onScoreChange={onScoreChange}
            />
          </Box>
        </ObjectEditField>
      </ObjectEditFieldGroup>
    </ObjectEditSection>
  );
}
